import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { googleLogin } from "../api/googleAuthApi";
import jwtTokenService from "../services/jwtTokenService";

const GoogleOneTap = ({ onSuccess }) => {
  const navigate = useNavigate();
  const hasInitialized = useRef(false);
  const oneTapShown = useRef(false);

  useEffect(() => {
    // Don't show if user is already logged in
    if (jwtTokenService.isAuthenticated()) {
      return;
    }

    // Prevent multiple initializations
    if (hasInitialized.current) {
      return;
    }

    const initializeGoogleOneTap = () => {
      if (!window.google || !window.google.accounts) {
        return;
      }

      hasInitialized.current = true;

      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
          context: "signin",
          ux_mode: "popup",
        });

        // Show One Tap after 5 seconds
        setTimeout(() => {
          if (!oneTapShown.current && !jwtTokenService.isAuthenticated()) {
            window.google.accounts.id.prompt((notification) => {
              if (notification.isNotDisplayed()) {
                // One Tap not displayed
              } else if (notification.isSkippedMoment()) {
                // One Tap skipped
              } else {
                oneTapShown.current = true;
              }
            });
          }
        }, 5000);
      } catch (error) {
        console.error("Error initializing Google One Tap:", error);
      }
    };

    // Load Google Identity Services script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleOneTap;
    document.body.appendChild(script);

    return () => {
      // Cleanup
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  const handleGoogleResponse = async (response) => {
    try {
      const result = await googleLogin(response.credential);

      if (result.data.success) {
        // Save token
        jwtTokenService.setToken(result.data.token);
        
        // Save user info
        if (result.data.user) {
          localStorage.setItem("trivana_user", JSON.stringify(result.data.user));
        }

        toast.success(`Welcome ${result.data.user?.name || 'User'}! 🎉`);

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess(result.data);
        }

        // Redirect to home
        setTimeout(() => {
          navigate("/");
          window.location.reload(); // Refresh to update navbar
        }, 500);
      }
    } catch (error) {
      console.error("Google login failed:", error);
      toast.error(
        error.response?.data?.message || "Google login failed. Please try again."
      );
    }
  };

  // This component doesn't render anything visible
  return null;
};

export default GoogleOneTap;
